import { memo, useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package } from 'lucide-react';
import { fetchModelRegistry, type RegistryModel } from './modelRegistryCache';


export const TrainedModelConfig = memo(({ data, onChange }: { data: any; onChange: (k: string, v: any) => void }) => {
    const [models, setModels] = useState<RegistryModel[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchModelRegistry().then(list => {
            setModels(list);
            setLoading(false);
        });
    }, []);

    const modelKey = data.modelName || data.modelId || '';

    return (
        <div className="space-y-6">
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1">
                    <Package className="h-3 w-3" /> Trained Model
                </h4>
                <p>
                    Outputs a model from the registry. Connect to Score, Predict, or Evaluate.
                    Select which model to use, or leave empty to output all trained models.
                </p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Model</Label>
                <Select
                    value={modelKey || '_all'}
                    onValueChange={(v) => {
                        const val = v === '_all' ? '' : v;
                        onChange('modelName', val);
                        onChange('modelId', val);
                    }}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={loading ? 'Loading...' : 'Select model...'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="_all">(All models in registry)</SelectItem>
                        {models.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                                {m.name}{' '}
                                <span className="text-gray-400 text-[10px] ml-1">({m.model_type})</span>
                            </SelectItem>
                        ))}
                        {!loading && models.length === 0 && (
                            <SelectItem value="_none" disabled>
                                No models in registry. Train one first.
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">
                    Select one model, or leave as &quot;All&quot; to output the full registry table.
                </p>
            </div>
        </div>
    );
});
TrainedModelConfig.displayName = 'TrainedModelConfig';
